<template>
  <section class="persona-charts" aria-label="분석 차트">
    <div v-if="items.length > 1" class="persona-chart-tabs" role="tablist" aria-label="분석 차트 선택">
      <button v-for="(chart,index) in items" :id="tabId(chart)" :key="chart.url" ref="tabs" type="button" role="tab" :aria-selected="selectedUrl===chart.url" :aria-controls="panelId(chart)" :tabindex="focusedUrl===chart.url?0:-1" @click="selectChart(chart)" @keydown="onTabKey($event,index)">
        {{ chart.name }}<small v-if="!allowed(chart)">{{ level(chart.authorityLevel) }}</small>
      </button>
    </div>
    <h3 v-else-if="selected" class="persona-chart-title">{{ selected.name }}</h3>
    <div v-if="!selected" class="persona-chart-notice">현재 연결된 차트가 없습니다.</div>
    <div v-else-if="!allowed(selected)" :id="panelId(selected)" class="persona-chart-notice" role="tabpanel" :aria-labelledby="items.length>1?tabId(selected):null">
      <template v-if="!authenticated"><strong>로그인하고 분석을 확인하세요.</strong><p>기존 익스퍼트 계정과 이용권으로 차트를 이용할 수 있습니다.</p><a :href="linkTo('/login')">로그인</a></template>
      <template v-else><strong>{{ level(selected.authorityLevel) }} 등급 이상 이용할 수 있는 분석입니다.</strong><p>현재 이용권에서 가능한 다른 차트를 선택하거나 이용권을 확인하세요.</p><a :href="linkTo('/pricing')">이용권 확인</a></template>
    </div>
    <div v-for="(entry,index) in slots" :key="index">
    <div v-if="entry" v-show="entry.chart.url===selectedUrl && allowed(entry.chart)" :id="panelId(entry.chart)" :key="entry.key" class="persona-chart-panel" role="tabpanel" :aria-labelledby="items.length>1 && items.some(chart=>chart.url===entry.chart.url)?tabId(entry.chart):null" :aria-label="entry.chart.name" :aria-busy="entry.state==='loading'">
      <div v-if="entry.state!=='ready'" class="persona-chart-notice" role="status" aria-live="polite">
        <template v-if="entry.state==='loading'"><strong>분석 차트를 불러오는 중입니다.</strong><p>기존 차트의 필터와 범례를 그대로 사용할 수 있습니다.</p></template>
        <template v-else-if="entry.state==='waiting'"><strong>차트를 불러오는 데 시간이 걸리고 있습니다.</strong><p>조금 더 기다리거나 이 차트만 다시 불러올 수 있습니다.</p><button type="button" @click="retry(entry)">차트 다시 불러오기</button></template>
        <template v-else-if="entry.state==='permission'"><strong>이 차트를 이용할 권한을 확인해 주세요.</strong><p>이용권에 따라 표시되는 분석이 다를 수 있습니다.</p><a :href="linkTo('/pricing')">이용권 확인</a></template>
        <template v-else><strong>차트를 불러오지 못했습니다.</strong><p>현재 단계에서 다시 시도할 수 있습니다.</p><button type="button" @click="retry(entry)">차트 다시 불러오기</button></template>
      </div>
      <!-- Match analyticsPlatform's literal mode attribute, including its legacy parameter semantics. -->
      <tableau-embedded v-if="allowed(entry.chart)" :ref="entry.key" :tableau-id="entry.chart.url" isCorpTableau="false" :inline="true" @chart-state="onChartState(entry,$event)" @chart-context="onChartContext(entry,$event)" />
    </div>
    </div>
  </section>
</template>

<script>
import TableauEmbedded from '../../components/TableauEmbedded.vue';

export default {
  name: 'PersonaCharts',
  components: { TableauEmbedded },
  props: { charts: { type: Array, default: () => [] }, contextKey: { type: String, default: '' }, active: { type: Boolean, default: true } },
  data: () => ({ selectedUrl: '', focusedUrl: '', retained: [], generation: 0, clock: 0 }),
  computed: {
    items() {
      const seen = new Set();
      return this.charts.filter(chart => chart && typeof chart.url === 'string' && chart.url && !seen.has(chart.url) && seen.add(chart.url))
        .map(chart => ({ ...chart, name: chart.originalname || chart.name || '분석 차트' }));
    },
    authContext() {
      const state = this.$store && this.$store.state || {}, profile = state.profile || {};
      return [state.isLoggedin, state.authorityLevel, profile.userId, profile.isCorp, state.refreshToken];
    },
    authenticated() { return !!(this.authContext[0] && this.authContext[2]); },
    authority() { return Number(this.authContext[1]) || 0; },
    regionContext() {
      const state = this.$store && this.$store.state || {}, area = state.bjdInfo || {};
      return JSON.stringify([area.bjdCode || '', area.sd || '', area.sgg || '', area.emd || '', state.priceType || '']);
    },
    selected() { return this.items.find(chart => chart.url === this.selectedUrl); },
    slots() { return [this.retained[0] || null, this.retained[1] || null, this.retained[2] || null]; },
    chartSignature() { return JSON.stringify(this.items.map(chart => [chart.url, chart.authorityLevel, chart.unavailable])); }
  },
  watch: {
    authContext: { immediate: true, handler() { this.clearTimers(); this.generation += 1; this.retained = []; this.chooseFirst(); } },
    active(value) { if (value) this.resumeSelected(); },
    contextKey() { this.chooseFirst(); },
    chartSignature() { this.chooseFirst(); }
  },
  activated() {
    this._routeDetached = false;
    this.resumeSelected();
  },
  deactivated() {
    // Route-level keep-alive can detach iframe documents. Re-authorize a view
    // when it is next visible; native top-level v-show tabs do not take this path.
    this._routeDetached = true;
    this.clearTimers();
    this.retained.forEach(entry => { entry.needsReload = true; });
  },
  beforeDestroy() { this.clearTimers(); },
  methods: {
    allowed(chart) { return !!(chart && !chart.unavailable && this.authenticated && Number.isFinite(Number(chart.authorityLevel)) && this.authority >= Number(chart.authorityLevel)); },
    level(value) { return ({100:'일반',200:'고급',300:'프리미엄'})[value] || '해당 이용권'; },
    linkTo(path) { return this.$router ? this.$router.resolve(path).href : 'https://expert.richgo.ai'+path; },
    tabId(chart) { return `persona-chart-tab-${this._uid}-${encodeURIComponent(chart.url)}`; },
    panelId(chart) { return `persona-chart-panel-${this._uid}-${encodeURIComponent(chart.url)}`; },
    chooseFirst() {
      // Preserve the DOM order of retained views: moving an iframe can reload it.
      this.retained.forEach(entry => { const current = this.items.find(chart => chart.url === entry.chart.url); if (current) entry.chart = current; });
      if (this.retained.some(entry => !this.allowed(entry.chart))) { this.clearTimers(); this.retained = []; }
      const chart = this.items.find(item => this.allowed(item)) || this.items[0];
      if (chart) this.selectChart(chart); else { this.selectedUrl = ''; this.focusedUrl = ''; }
    },
    selectChart(chart) {
      const returning = this.selectedUrl !== chart.url;
      this.selectedUrl = chart.url;
      this.focusedUrl = chart.url;
      if (!this.allowed(chart)) return;
      const retained = this.retained.find(entry => entry.chart.url === chart.url);
      if (retained) {
        retained.lastUsed = ++this.clock;
        // The original regional selector is shared via Vuex. Re-apply changed
        // external conditions on return; workbook-only filters stay untouched.
        if (returning) this.resumeSelected();
        return;
      }
      if (!this.active || this._routeDetached) return;
      const entry = { chart, key:`persona-viz-${this.generation}-${++this.clock}`, generation:this.generation, lastUsed:this.clock, state:'loading', regionContext:null, usesSharedRegion:true, refreshingContext:null, needsReload:!!this._routeDetached };
      if (this.retained.length < 3) this.retained.push(entry);
      else {
        const oldest = this.retained.reduce((one,two) => one.lastUsed < two.lastUsed ? one : two);
        this.clearTimer(oldest);
        // Fixed host slots prevent Vue's keyed-list diff from moving surviving iframes.
        this.$set(this.retained, this.retained.indexOf(oldest), entry);
      }
      this.startTimer(entry);
    },
    onChartState(entry,event) {
      if (!this.retained.includes(entry) || entry.generation !== this.generation || !this.allowed(entry.chart) || event.view !== entry.chart.url) return;
      if (['loading','ready','error','permission'].includes(event.state)) {
        entry.state = event.state;
        if (event.state === 'loading') this.startTimer(entry); else { this.clearTimer(entry); entry.refreshingContext = null; }
      }
    },
    retry(entry) {
      if (!this.retained.includes(entry) || !this.allowed(entry.chart) || entry.state === 'loading') return;
      this.refreshEntry(entry);
    },
    onChartContext(entry,event) {
      if (this.retained.includes(entry) && entry.generation === this.generation && event.view === entry.chart.url) { entry.regionContext = event.signature; entry.usesSharedRegion = event.usesSharedRegion !== false; }
    },
    resumeSelected() {
      if (!this.active || this._routeDetached) return;
      const entry = this.retained.find(item => item.chart.url === this.selectedUrl);
      if (!entry) { if (this.selected && this.allowed(this.selected)) this.selectChart(this.selected); return; }
      this.$nextTick(() => {
        if (!this.active || this._routeDetached || entry.chart.url !== this.selectedUrl || !this.retained.includes(entry)) return;
        const changed = entry.usesSharedRegion && entry.regionContext !== null && entry.regionContext !== this.regionContext;
        if (entry.needsReload || (changed && !(entry.state === 'loading' && entry.refreshingContext === this.regionContext))) this.refreshEntry(entry);
      });
    },
    refreshEntry(entry) {
      if (!this.retained.includes(entry) || !this.allowed(entry.chart)) return;
      const refs = this.$refs[entry.key], embed = Array.isArray(refs) ? refs[0] : refs;
      if (embed) { entry.state = 'loading'; entry.needsReload = false; entry.refreshingContext = this.regionContext; this.startTimer(entry); embed.refresh(); }
    },
    clearTimer(entry) {
      if (!this._loadTimers) return;
      clearTimeout(this._loadTimers.get(entry.key));
      this._loadTimers.delete(entry.key);
    },
    clearTimers() {
      if (this._loadTimers) this._loadTimers.forEach(timer => clearTimeout(timer));
      this._loadTimers = new Map();
    },
    startTimer(entry) {
      this.clearTimer(entry);
      if (this._routeDetached) return;
      this._loadTimers.set(entry.key, setTimeout(() => {
        this._loadTimers.delete(entry.key);
        if (this.retained.includes(entry) && entry.generation === this.generation && entry.state === 'loading') entry.state = 'waiting';
      }, 25000));
    },
    onTabKey(event,index) {
      const last = this.items.length - 1;
      const next = ({ArrowRight:(index+1) % this.items.length, ArrowLeft:(index+last) % this.items.length, Home:0, End:last})[event.key];
      if (next === undefined) return;
      event.preventDefault();
      this.focusedUrl = this.items[next].url;
      this.$nextTick(() => { const tab = this.$refs.tabs && this.$refs.tabs[next]; if (tab) tab.focus(); });
    }
  }
};
</script>

<style scoped>
.persona-charts { min-width: 0; width: 100%; color: #202b3b; }
.persona-chart-title { margin: 0 0 16px; font-size: 16px; font-weight: 650; }
.persona-chart-tabs { display: flex; flex-wrap: wrap; gap: 4px 20px; border-bottom: 1px solid #dce1e7; margin-bottom: 20px; }
.persona-chart-tabs button { min-height: 44px; padding: 10px 2px; border-bottom: 2px solid transparent; color: #526174; font: inherit; font-size: 14px; text-align: left; }
.persona-chart-tabs button[aria-selected="true"] { border-color: #165bd8; color: #165bd8; font-weight: 650; }
.persona-chart-tabs small { margin-left: 8px; font-size: 13px; color: #526174; }
.persona-chart-tabs button:focus-visible,.persona-chart-notice a:focus-visible,.persona-chart-notice button:focus-visible { outline: 2px solid #165bd8; outline-offset: 3px; }
.persona-chart-panel { min-width: 0; }
.persona-chart-notice { padding: 22px 0; font-size: 14px; line-height: 1.7; }
.persona-chart-notice strong { font-size: 15px; font-weight: 650; }
.persona-chart-notice p { margin: 4px 0 12px; color: #526174; }
.persona-chart-notice a,.persona-chart-notice button { display: inline-block; padding: 7px 12px; border: 1px solid #b8c2ce; border-radius: 4px; color: #165bd8; font: inherit; font-weight: 600; text-decoration: none; }
</style>
